import { useId } from 'react';
import { FiAlertTriangle } from 'react-icons/fi';
import SelectTextField from '../../../molecules/SelectTextField';
import TextEdit from '../../../molecules/TextEdit';
import DateField from '../../../molecules/DateField';
import { getErrorMessage } from '../../../../utils/formErrorUtils';
import { CaseEventType, type SelectOption } from '../../../../types/LegalCase';
import type { ErrorField } from '../../../../types/ErrorField';
import useLegalCaseEventForm, { residualErrors } from '../../../../hooks/useLegalCaseEventForm';
import type { AddCaseEventValue } from '../../../../hooks/useLegalCaseEvents';

interface LegalCaseCaseEventFormProps {
    typeOptions: SelectOption[];
    errorFields: ErrorField[];
    isSaving: boolean;
    onSubmit: (value: AddCaseEventValue) => Promise<boolean>;
    onCancel: () => void;
}

// Só JSX + bindings: estado e regras do formulário vivem em useLegalCaseEventForm.
const LegalCaseCaseEventForm = ({
    typeOptions,
    errorFields,
    isSaving,
    onSubmit,
    onCancel,
}: LegalCaseCaseEventFormProps) => {
    const form = useLegalCaseEventForm({ onSubmit });
    const textId = useId();

    return (
        <div className="rounded-lg border border-gray-200 dark:border-slate-700 p-3 space-y-3">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <SelectTextField
                    label="Tipo"
                    items={typeOptions}
                    value={form.eventType}
                    // O select só oferece tipos válidos (page-form ou fallback local), então
                    // o narrowing string→CaseEventType é seguro.
                    onChange={(e) => form.setEventType(e.target.value as CaseEventType)}
                    errorMessage={getErrorMessage(errorFields, 'eventType')}
                />
                <DateField
                    label="Data do fato"
                    value={form.eventDate}
                    onChange={form.setEventDate}
                    errorMessage={getErrorMessage(errorFields, 'eventDate')}
                />
            </div>

            <div>
                <label htmlFor={textId} className="block text-sm font-medium text-gray-600 dark:text-gray-300 mb-1">
                    Andamento
                </label>
                <textarea
                    id={textId}
                    value={form.text}
                    onChange={(e) => form.setText(e.target.value)}
                    rows={3}
                    placeholder="O que aconteceu no processo"
                    className="w-full rounded-md border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-800 px-3 py-2 text-sm text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:border-blue-600 dark:focus:border-blue-400"
                />
                {getErrorMessage(errorFields, 'text') && (
                    <p className="mt-1 text-sm text-red-600 dark:text-red-400">
                        {getErrorMessage(errorFields, 'text')}
                    </p>
                )}
            </div>

            <TextEdit
                label="Referência de folhas (opcional)"
                value={form.sheetReference}
                onChange={(e) => form.setSheetReference(e.target.value)}
                errorMessage={getErrorMessage(errorFields, 'sheetReference')}
            />

            {/* Erros de validação (422) sem campo próprio na tela — não deixa sumir. */}
            {residualErrors(errorFields).map((e) => (
                <div
                    key={e.fieldName}
                    className="flex items-center gap-2 text-sm text-red-700 dark:text-red-400"
                >
                    <FiAlertTriangle size={14} />
                    <span>{e.message}</span>
                </div>
            ))}

            <div className="flex items-center justify-end gap-2">
                <button
                    onClick={onCancel}
                    className="px-3 py-1.5 text-sm text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-slate-700 rounded-lg"
                >
                    Cancelar
                </button>
                <button
                    onClick={form.submit}
                    disabled={isSaving || !form.canSubmit}
                    className="px-3 py-1.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium disabled:opacity-50"
                >
                    {isSaving ? 'Salvando...' : 'Registrar andamento'}
                </button>
            </div>
        </div>
    );
};

export default LegalCaseCaseEventForm;
